/**
 * useTenantTier – Hook om subscription tier van de tenant op te halen.
 * Gebruikt /auth/me die subscription_tier (integer) retourneert.
 * STUDIO=10, ACADEMY=20, MASTER=30, ENTERPRISE=40
 */

import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'

const TIER_VALUES = {
  STUDIO: 10,
  ACADEMY: 20,
  MASTER: 30,
  ENTERPRISE: 40,
} as const

export type TierName = keyof typeof TIER_VALUES

export function useTenantTier() {
  const user = useAuthStore((state) => state.user)

  // Admin/eigenaar passeert ALTIJD elke tier-gate (is_superuser, geen tenant_id,
  // is_admin of role=ADMIN). Consistent met useFeature/rbac.require_tier — de
  // eigenaar betaalt niet aan zichzelf. Zie geheugen feedback_admin_rechten.
  const isAdmin = !!(
    user?.is_superuser ||
    !user?.tenant_id ||
    user?.is_admin ||
    String(user?.role || '').toUpperCase() === 'ADMIN'
  )

  const { data: userDetails, isLoading } = useQuery({
    queryKey: ['auth', 'me', user?.id],
    queryFn: async () => {
      const res = await api.get('/auth/me', { timeout: 8000 })
      return res?.data ?? null
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const tierValue = userDetails?.subscription_tier ?? TIER_VALUES.STUDIO
  const tierName = (Object.keys(TIER_VALUES) as TierName[]).find(
    (k) => TIER_VALUES[k] === tierValue
  ) ?? 'STUDIO'

  return {
    tierValue: typeof tierValue === 'number' ? tierValue : TIER_VALUES.STUDIO,
    tierName,
    isAdmin,
    // Admin hoeft niet op /auth/me te wachten — nooit een upgrade-muur tonen.
    isLoading: isAdmin ? false : (!!user && isLoading),
    hasTier: (required: TierName) =>
      isAdmin ||
      (typeof tierValue === 'number' ? tierValue : TIER_VALUES.STUDIO) >=
      TIER_VALUES[required],
  }
}
